import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { RiDeleteBinLine } from '@remixicon/react';
import {
  Button,
  ErrorState,
  Footnote,
  FormGroup,
  FormRow,
  Group,
  Notice,
  Row,
  SectionLabel,
  Segmented,
  Skeleton,
} from '@store/ui';
import { haptic } from '../../telegram';
import { useAsync } from '../shared';
import {
  adminCategories,
  adminProduct,
  createProduct,
  createVariant,
  archiveVariant,
  newIdempotencyKey,
  patchProduct,
  patchVariant,
  type AdminProductDetail,
  type FulfillmentMode,
  type ProductStatus,
  type ProductWrite,
  type VariantWrite,
} from './client';
import { blockingIssues } from './diagnose';
import {
  ActionBar,
  AdminHeader,
  AreaRow,
  ChoiceRows,
  ICON_BUTTON_DANGER,
  TextRow,
  ToggleRow,
  useSubmit,
} from './fields';
import { fromMinorUnits, isTypedAmount, toMinorUnits } from './money';
import { PreviewSection } from './preview';
import { VariantsSection, newVariantKey, type VariantDraft } from './variants';

// --- Добавить товар / редактирование ----------------------------------------

/**
 * One form in six sections, and deliberately not a wizard.
 *
 * Every section is reachable from every other one at any moment: the owner
 * writing the description remembers the price is wrong, taps «Цена», fixes it
 * and comes straight back. That is why the whole state lives here and the
 * sections only render it — switching a segment cannot lose a keystroke,
 * because nothing is mounted per step and nothing is submitted per step.
 */

type Section = 'basic' | 'price' | 'images' | 'variants' | 'stock' | 'preview';

/* Mutable because `Segmented` takes a plain array; nothing writes to it. */
const SECTIONS: Array<{ value: Section; label: string }> = [
  { value: 'basic', label: 'Основное' },
  { value: 'price', label: 'Цена' },
  { value: 'images', label: 'Изображения' },
  { value: 'variants', label: 'Варианты' },
  { value: 'stock', label: 'Склад' },
  { value: 'preview', label: 'Предпросмотр' },
];

const STATUS_OPTIONS: ReadonlyArray<{ value: ProductStatus; label: string; subtitle: string }> = [
  { value: 'draft', label: 'Черновик', subtitle: 'Виден только вам' },
  { value: 'published', label: 'Опубликован', subtitle: 'Продаётся в магазине' },
  { value: 'hidden', label: 'Скрыт', subtitle: 'Не в каталоге, но ссылка работает' },
  { value: 'archived', label: 'В архиве', subtitle: 'Убран из магазина совсем' },
];

const FULFILLMENT_OPTIONS: ReadonlyArray<{
  value: FulfillmentMode;
  label: string;
  subtitle: string;
}> = [
  { value: 'automatic', label: 'Автоматически', subtitle: 'Ключ уходит покупателю сразу' },
  { value: 'manual', label: 'Вручную', subtitle: 'Заказ ждёт оператора' },
  { value: 'none', label: 'Без выдачи', subtitle: 'Услуга, ничего не отправляется' },
];

interface Attribute {
  key: string;
  name: string;
  value: string;
}

/*
 * Geometry that is a correctness property rather than a theme choice, kept here
 * for the same reason `ServiceTile` keeps its mark's: a pair of inputs that do
 * not share the row evenly, or a thumbnail strip with no scroller, is broken in
 * every stylesheet. The class names are emitted for the CSS agent to dress.
 */
const ATTR_ROW: CSSProperties = { display: 'flex', gap: 'var(--space-2)', alignItems: 'center' };
const ATTR_INPUT: CSSProperties = { flex: 1, minWidth: 0 };
const MEDIA_STRIP: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
  overflowX: 'auto',
  paddingBottom: 'var(--space-1)',
};
const MEDIA_ITEM: CSSProperties = {
  width: 96,
  height: 96,
  flexShrink: 0,
  objectFit: 'cover',
  borderRadius: 'var(--radius-xs)',
  background: 'var(--surface-pressed)',
};

interface FormState {
  title: string;
  subtitle: string;
  description: string;
  activationInstructions: string;
  status: ProductStatus;
  categoryId: string | null;
  price: string;
  listPrice: string;
  sortOrder: string;
  isFeatured: boolean;
  images: string[];
  attributes: Attribute[];
  fulfillmentMode: FulfillmentMode;
  tracksInventory: boolean;
  lowStockThreshold: string;
  purchaseLimitPerUser: string;
  variants: VariantDraft[];
}

const blankForm = (): FormState => ({
  title: '',
  subtitle: '',
  description: '',
  activationInstructions: '',
  status: 'draft',
  categoryId: null,
  price: '',
  listPrice: '',
  sortOrder: '',
  isFeatured: false,
  images: [''],
  attributes: [],
  fulfillmentMode: 'automatic',
  /*
   * Off for a new product, against the column's own default.
   *
   * `products.tracks_inventory` defaults to true, which means a product created
   * and published in one sitting is unbuyable the moment it appears: checkout
   * raises `outOfStock` before it will even price it, and the shop's own owner
   * sees «Нет в наличии» on the thing they just made. Loading stock is a
   * separate step and a deliberate one; a shop that wants it turns this on and
   * is then told, in the footnote below, exactly what it now owes.
   */
  tracksInventory: false,
  lowStockThreshold: '',
  purchaseLimitPerUser: '',
  variants: [],
});

const numberOrNull = (input: string): number | null => {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
};

function formFromDetail(detail: AdminProductDetail, decimals: number): FormState {
  return {
    title: detail.title,
    subtitle: detail.subtitle ?? '',
    description: detail.description ?? '',
    activationInstructions: detail.activationInstructions ?? '',
    status: detail.status,
    categoryId: detail.categoryId,
    price: fromMinorUnits(detail.basePrice.amount, decimals),
    listPrice: detail.listPrice ? fromMinorUnits(detail.listPrice.amount, decimals) : '',
    sortOrder: detail.sortOrder === undefined ? '' : String(detail.sortOrder),
    isFeatured: detail.isFeatured ?? false,
    images: (detail.media ?? []).map((item) => item.url).concat(''),
    attributes: Object.entries(detail.attributes ?? {}).map(([name, value]) => ({
      key: newVariantKey(),
      name,
      value,
    })),
    fulfillmentMode: detail.fulfillmentMode,
    tracksInventory: detail.tracksInventory,
    lowStockThreshold:
      detail.lowStockThreshold === undefined ? '' : String(detail.lowStockThreshold),
    purchaseLimitPerUser:
      detail.purchaseLimitPerUser === undefined || detail.purchaseLimitPerUser === null
        ? ''
        : String(detail.purchaseLimitPerUser),
    variants: (detail.variants ?? []).map((variant) => ({
      key: newVariantKey(),
      id: variant.id,
      title: variant.title,
      price: fromMinorUnits(variant.price.amount, variant.price.decimals),
      durationDays: variant.durationDays === null ? '' : String(variant.durationDays),
      isPublished: variant.isPublished,
      available: variant.available,
      removed: false,
    })),
  };
}

/**
 * Only what the owner actually changed.
 *
 * A PATCH that restates every field is how «Опубликовать» switched stock
 * counting back on for the whole catalogue — the route has since dropped its
 * zod defaults, but sending a field nobody touched is still a write nobody
 * asked for. It matters twice as much on the fallback read, where several
 * fields never arrived: an empty box there means "unknown", not "cleared".
 */
function diffProduct(
  before: FormState | null,
  after: FormState,
  currency: string,
  decimals: number,
): ProductWrite {
  const body: ProductWrite = {};
  const changed = (key: keyof FormState) => before === null || before[key] !== after[key];

  if (changed('title')) body.title = after.title.trim();
  if (changed('subtitle')) body.subtitle = after.subtitle.trim() || null;
  if (changed('description')) body.description = after.description.trim() || null;
  if (changed('activationInstructions')) {
    body.activationInstructions = after.activationInstructions.trim() || null;
  }
  if (changed('status')) body.status = after.status;
  if (changed('categoryId')) body.categoryId = after.categoryId;
  if (changed('fulfillmentMode')) body.fulfillmentMode = after.fulfillmentMode;
  if (changed('tracksInventory')) body.tracksInventory = after.tracksInventory;
  if (changed('isFeatured')) body.isFeatured = after.isFeatured;

  if (changed('price')) {
    const amount = toMinorUnits(after.price, decimals);
    if (amount !== null) {
      body.basePrice = amount;
      body.baseCurrency = currency;
    }
  }
  if (changed('listPrice')) {
    body.listPrice = after.listPrice.trim() === '' ? null : toMinorUnits(after.listPrice, decimals);
  }
  if (changed('sortOrder')) {
    const value = numberOrNull(after.sortOrder);
    if (value !== null) body.sortOrder = value;
  }
  if (changed('lowStockThreshold')) {
    const value = numberOrNull(after.lowStockThreshold);
    if (value !== null) body.lowStockThreshold = value;
  }
  if (changed('purchaseLimitPerUser')) {
    body.purchaseLimitPerUser = numberOrNull(after.purchaseLimitPerUser);
  }

  const media = after.images
    .map((url) => url.trim())
    .filter(Boolean)
    .map((url, index) => ({ url, alt: null, sortOrder: index }));
  const mediaBefore = (before?.images ?? []).map((url) => url.trim()).filter(Boolean);
  if (
    before === null ||
    mediaBefore.join('\u0000') !== media.map((item) => item.url).join('\u0000')
  ) {
    body.media = media;
  }

  const attributes = Object.fromEntries(
    after.attributes
      .filter((attribute) => attribute.name.trim() !== '')
      .map((attribute) => [attribute.name.trim(), attribute.value.trim()]),
  );
  const attributesBefore = Object.fromEntries(
    (before?.attributes ?? [])
      .filter((attribute) => attribute.name.trim() !== '')
      .map((attribute) => [attribute.name.trim(), attribute.value.trim()]),
  );
  if (JSON.stringify(attributes) !== JSON.stringify(attributesBefore)) body.attributes = attributes;

  return body;
}

const variantBody = (variant: VariantDraft, currency: string, decimals: number): VariantWrite => ({
  title: variant.title.trim(),
  price: toMinorUnits(variant.price, decimals) ?? '0',
  currency,
  durationDays: numberOrNull(variant.durationDays),
  isPublished: variant.isPublished,
});

const HTTPS = /^https:\/\/\S+$/i;

export function AdminProductFormScreen({
  productId,
  onNavigate,
}: {
  readonly productId: string | null;
  readonly onNavigate: (route: string) => void;
}) {
  const detail = useAsync<AdminProductDetail | null>(
    () => (productId ? adminProduct(productId) : Promise.resolve(null)),
    [productId],
  );
  const categories = useAsync(() => adminCategories(), []);

  const [section, setSection] = useState<Section>('basic');
  const [form, setForm] = useState<FormState>(blankForm);
  /* The server's version, kept so the save can send only the difference. */
  const [loaded, setLoaded] = useState<FormState | null>(null);
  const [saved, setSaved] = useState<{
    id: string;
    shortId: string;
    published: boolean;
    tracksInventory: boolean;
  } | null>(null);
  const { busy, error, setError, run } = useSubmit();

  /*
   * One key per attempt, held across retries.
   *
   * A save that failed on the network may well have reached the server, so the
   * second press has to be recognisable as the same intent — otherwise a flaky
   * connection is how the catalogue grows two of everything. Replaced only once
   * a create has succeeded, because from then on it is a different product.
   */
  const idempotency = useRef(newIdempotencyKey());
  const savedRef = useRef<HTMLElement | null>(null);

  const currency = detail.data?.basePrice.currency ?? 'USD';
  const decimals = detail.data?.basePrice.decimals ?? 2;

  /*
   * The product this form is now editing.
   *
   * After a create it is the product that was just made — not null any more.
   * Without this the button still said «Создать товар» over a product that
   * already exists, and a second press made a second one: the idempotency key
   * is retired on success precisely because from then on it *would* be a
   * different product.
   */
  const editingId = productId ?? saved?.id ?? null;

  /* The confirmation is at the bottom of a long form, so on a phone it lands
     entirely below the fold and a successful save looks like nothing happened. */
  useEffect(() => {
    if (saved) savedRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [saved]);

  useEffect(() => {
    if (!detail.data) return;
    const next = formFromDetail(detail.data, detail.data.basePrice.decimals);
    setForm(next);
    setLoaded(next);
  }, [detail.data]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  const categoryList = categories.data?.categories ?? [];

  const priceOk = isTypedAmount(form.price, decimals);
  const listPriceOk = form.listPrice.trim() === '' || isTypedAmount(form.listPrice, decimals);
  const firstImage = form.images.map((url) => url.trim()).find(Boolean) ?? '';

  /** What is still missing before this may be shown to a customer. */
  const missing = useMemo(() => {
    const gaps: string[] = [];
    if (form.title.trim() === '') gaps.push('название');
    if (form.subtitle.trim() === '') gaps.push('краткое описание');
    if (form.description.trim() === '') gaps.push('полное описание');
    if (!priceOk) gaps.push('базовая цена');
    if (form.categoryId === null) gaps.push('категория');
    if (firstImage === '') gaps.push('изображение');
    return gaps;
  }, [form, priceOk, firstImage]);

  const badImages = form.images
    .map((url) => url.trim())
    .filter((url) => url !== '' && !HTTPS.test(url));

  const variantProblems = form.variants
    .filter((variant) => !variant.removed)
    .filter(
      (variant) => variant.title.trim() === '' || !isTypedAmount(variant.price, decimals),
    ).length;

  /* A draft may be as unfinished as the owner likes; the shop may not. */
  const blockers: string[] = [];
  if (form.title.trim() === '') blockers.push('Без названия товар не создать.');
  if (!priceOk) blockers.push('Базовая цена не заполнена или введена неверно.');
  if (!listPriceOk) blockers.push('Старая цена введена неверно.');
  if (badImages.length > 0) blockers.push('Ссылки на картинки должны начинаться с https://.');
  if (variantProblems > 0) blockers.push('У варианта не заполнено название или цена.');
  /*
   * The completeness gate guards the *transition*, not every later save.
   *
   * Applied to any save of a published product it becomes a trap: a live
   * product with no picture could no longer be edited at all — not even to fix
   * its title, and not even to give it the picture, because the button that
   * saves the picture is the one being refused. So it fires when something is
   * being put into the shop, and a product already in it stays editable with
   * the warning standing.
   */
  const publishing = form.status === 'published' && loaded?.status !== 'published';
  if (publishing && missing.length > 0) {
    blockers.push(`Чтобы опубликовать, заполните: ${missing.join(', ')}.`);
  }

  const issues = blockingIssues({
    status: form.status,
    tracksInventory: form.tracksInventory,
    fulfillmentMode: form.fulfillmentMode,
    basePriceMinor: toMinorUnits(form.price, decimals),
    categoryId: form.categoryId,
    available: detail.data?.available ?? null,
    variants: form.variants
      .filter((variant) => !variant.removed)
      .map((variant) => ({ title: variant.title, available: variant.available })),
  });

  const save = () =>
    run(async () => {
      const body = diffProduct(loaded, form, currency, decimals);

      let id = editingId;
      let shortId = saved?.shortId || detail.data?.shortId || '';

      if (id === null) {
        const created = await createProduct(
          { ...body, basePrice: toMinorUnits(form.price, decimals) ?? '0', baseCurrency: currency },
          idempotency.current,
        );
        id = created.product.id;
        shortId = created.product.shortId;
        idempotency.current = newIdempotencyKey();
        /* The baseline moves with it: everything in the form is now what the
           server holds, so the next save is a diff again rather than a full
           restatement of fields nobody touched. */
        setLoaded(form);
      } else if (Object.keys(body).length > 0) {
        const updated = await patchProduct(id, body);
        shortId = updated.product.shortId || shortId;
      }

      /*
       * Variants after the product, and one at a time.
       *
       * A new product has no id until it exists, so its options cannot be
       * created in the same call; and running them in parallel makes the
       * ordering — which is what `sortOrder` is for — depend on which request
       * the network happened to finish first.
       */
      let order = 0;
      for (const variant of form.variants) {
        if (variant.removed) {
          if (variant.id) await archiveVariant(id, variant.id);
          continue;
        }
        const payload = { ...variantBody(variant, currency, decimals), sortOrder: order };
        order += 1;
        if (variant.id === null) await createVariant(id, payload, newIdempotencyKey());
        else await patchVariant(id, variant.id, payload);
      }

      haptic.success();
      setSaved({
        id,
        shortId,
        published: form.status === 'published',
        tracksInventory: form.tracksInventory,
      });
      /* Re-read rather than assume: what the server stored is the truth, and
         this is also what makes the variant ids real for the next save. */
      if (productId) detail.reload();
    });

  if (detail.loading) {
    return (
      <div className="container stack">
        <Skeleton height={34} width="60%" />
        <Skeleton height={42} />
        <Skeleton height={220} />
      </div>
    );
  }

  if (productId && detail.error) {
    return (
      <div className="container stack">
        <AdminHeader title="Товар" />
        <ErrorState title="Товар не открылся" text={detail.error} onRetry={detail.reload} />
      </div>
    );
  }

  return (
    <div className="container stack">
      <AdminHeader
        title={editingId ? 'Редактирование' : 'Новый товар'}
        subtitle={editingId ? form.title || null : 'Черновик можно сохранить и дополнить позже'}
      />

      {detail.data?.partial ? (
        <Notice tone="warning">
          Полная карточка товара с сервера не пришла — часть полей показана пустой. Сохраняются
          только те поля, которые вы измените, поэтому остальные не пострадают.
        </Notice>
      ) : null}

      <Segmented options={SECTIONS} value={section} onChange={setSection} label="Раздел карточки" />

      {section === 'basic' ? (
        <div className="stack">
          <FormGroup>
            <TextRow
              label="Название"
              id="p-title"
              value={form.title}
              onChange={(value) => set('title', value)}
              placeholder="Spotify Premium"
              maxLength={200}
              invalid={form.title.trim() === ''}
            />
            <AreaRow
              label="Краткое описание"
              id="p-subtitle"
              value={form.subtitle}
              onChange={(value) => set('subtitle', value)}
              placeholder="Одна строка под названием в списке"
              rows={2}
              maxLength={200}
            />
            <AreaRow
              label="Полное описание"
              id="p-description"
              value={form.description}
              onChange={(value) => set('description', value)}
              placeholder="Что покупатель получает и как это работает"
              rows={6}
              maxLength={8000}
            />
            <AreaRow
              label="Инструкция после покупки"
              id="p-instructions"
              value={form.activationInstructions}
              onChange={(value) => set('activationInstructions', value)}
              placeholder="Показывается только после оплаты"
              rows={4}
              maxLength={8000}
            />
          </FormGroup>

          <section>
            <SectionLabel>Характеристики</SectionLabel>
            {form.attributes.length === 0 ? (
              <Footnote>Пары «название — значение», например «Регион — Россия».</Footnote>
            ) : (
              <FormGroup>
                {form.attributes.map((attribute, index) => (
                  <FormRow key={attribute.key} label={`Характеристика ${index + 1}`} stacked>
                    <div className="attrrow" style={ATTR_ROW}>
                      <input
                        style={ATTR_INPUT}
                        aria-label={`Название характеристики ${index + 1}`}
                        value={attribute.name}
                        placeholder="Регион"
                        onChange={(event) =>
                          set(
                            'attributes',
                            form.attributes.map((item) =>
                              item.key === attribute.key
                                ? { ...item, name: event.target.value }
                                : item,
                            ),
                          )
                        }
                      />
                      <input
                        style={ATTR_INPUT}
                        aria-label={`Значение характеристики ${index + 1}`}
                        value={attribute.value}
                        placeholder="Россия"
                        onChange={(event) =>
                          set(
                            'attributes',
                            form.attributes.map((item) =>
                              item.key === attribute.key
                                ? { ...item, value: event.target.value }
                                : item,
                            ),
                          )
                        }
                      />
                      <button
                        type="button"
                        className="iconbutton iconbutton--danger"
                        style={ICON_BUTTON_DANGER}
                        aria-label={`Удалить характеристику ${attribute.name || index + 1}`}
                        onClick={() =>
                          set(
                            'attributes',
                            form.attributes.filter((item) => item.key !== attribute.key),
                          )
                        }
                      >
                        <RiDeleteBinLine size={18} aria-hidden="true" />
                      </button>
                    </div>
                  </FormRow>
                ))}
              </FormGroup>
            )}
            <div style={{ marginTop: 'var(--space-3)' }}>
              <Button
                variant="secondary"
                block
                onClick={() =>
                  set('attributes', [
                    ...form.attributes,
                    { key: newVariantKey(), name: '', value: '' },
                  ])
                }
              >
                Добавить характеристику
              </Button>
            </div>
          </section>

          <section>
            <SectionLabel>Статус</SectionLabel>
            <ChoiceRows
              options={STATUS_OPTIONS}
              value={form.status}
              onChange={(value) => set('status', value)}
            />
            {form.status === 'published' && missing.length > 0 ? (
              <Footnote>Не хватает: {missing.join(', ')}.</Footnote>
            ) : null}
          </section>
        </div>
      ) : null}

      {section === 'price' ? (
        <div className="stack">
          <FormGroup>
            <TextRow
              label="Цена, $"
              id="p-price"
              value={form.price}
              onChange={(value) => set('price', value)}
              placeholder="1990"
              inputMode="decimal"
              invalid={form.price.trim() !== '' && !priceOk}
            />
            <TextRow
              label="Старая цена, $"
              id="p-listprice"
              value={form.listPrice}
              onChange={(value) => set('listPrice', value)}
              placeholder="Необязательно"
              inputMode="decimal"
              invalid={!listPriceOk}
            />
          </FormGroup>
          <Footnote>
            Рубли, копейки через запятую. На сервер уходит целое число копеек — округления по дороге
            не будет.
          </Footnote>

          <section>
            <SectionLabel>Категория</SectionLabel>
            {categories.loading ? (
              <Skeleton height={120} />
            ) : categoryList.length === 0 ? (
              <Group>
                <Row
                  title="Категорий пока нет"
                  subtitle="Товар без категории не появится на главной"
                  chevron
                  onClick={() => onNavigate('/admin/categories/new')}
                />
              </Group>
            ) : (
              <Group>
                {categoryList.map((category) => (
                  <Row
                    key={category.id}
                    title={category.title}
                    {...(category.isPublished ? {} : { subtitle: 'Категория скрыта' })}
                    selected={category.id === form.categoryId}
                    onClick={() => {
                      haptic.select();
                      set('categoryId', category.id === form.categoryId ? null : category.id);
                    }}
                  />
                ))}
              </Group>
            )}
          </section>

          <FormGroup>
            <TextRow
              label="Порядок"
              id="p-sort"
              value={form.sortOrder}
              onChange={(value) => set('sortOrder', value)}
              placeholder="0"
              inputMode="numeric"
            />
          </FormGroup>
          <Footnote>Меньший порядок — выше в списке.</Footnote>

          <Group>
            <ToggleRow
              title="Показывать на главной"
              subtitle="Товар попадёт в подборку над категориями"
              value={form.isFeatured}
              onChange={(value) => set('isFeatured', value)}
            />
          </Group>
        </div>
      ) : null}

      {section === 'images' ? (
        <div className="stack">
          <Footnote>
            Загрузки файлов в магазине пока нет: картинка — это ссылка, которую вы вставляете. Она
            должна начинаться с https://, иначе Telegram её не покажет. Первая ссылка — главная.
          </Footnote>

          <FormGroup>
            {form.images.map((url, index) => (
              <TextRow
                key={index}
                label={index === 0 ? 'Главная' : `Ещё ${index}`}
                id={`p-image-${index}`}
                value={url}
                onChange={(value) =>
                  set(
                    'images',
                    form.images.map((item, position) => (position === index ? value : item)),
                  )
                }
                placeholder="https://…"
                inputMode="url"
                invalid={url.trim() !== '' && !HTTPS.test(url.trim())}
              />
            ))}
          </FormGroup>

          <div className="row" style={{ gap: 'var(--space-2)' }}>
            <Button variant="secondary" block onClick={() => set('images', [...form.images, ''])}>
              Ещё ссылка
            </Button>
            {/* Named for what it does: it is the *last* field that goes, and
                the owner may be looking at three of them. */}
            {form.images.length > 1 ? (
              <Button variant="ghost" onClick={() => set('images', form.images.slice(0, -1))}>
                Убрать последнюю
              </Button>
            ) : null}
          </div>

          {form.images.some((url) => HTTPS.test(url.trim())) ? (
            <section>
              <SectionLabel>Как это выглядит</SectionLabel>
              <div className="mediastrip" style={MEDIA_STRIP}>
                {form.images
                  .map((url) => url.trim())
                  .filter((url) => HTTPS.test(url))
                  .map((url) => (
                    <img
                      key={url}
                      className="mediastrip__item"
                      style={MEDIA_ITEM}
                      src={url}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      referrerPolicy="no-referrer"
                    />
                  ))}
              </div>
            </section>
          ) : null}
        </div>
      ) : null}

      {section === 'variants' ? (
        <VariantsSection
          variants={form.variants}
          decimals={decimals}
          onChange={(next) => set('variants', next)}
        />
      ) : null}

      {section === 'stock' ? (
        <div className="stack">
          <section>
            <SectionLabel>Способ выдачи</SectionLabel>
            <ChoiceRows
              options={FULFILLMENT_OPTIONS}
              value={form.fulfillmentMode}
              onChange={(value) => set('fulfillmentMode', value)}
            />
          </section>

          <section>
            <SectionLabel>Учёт остатков</SectionLabel>
            <Group>
              <ToggleRow
                title="Считать остатки"
                subtitle="Магазин откажет в оплате, когда склад пуст"
                value={form.tracksInventory}
                onChange={(value) => set('tracksInventory', value)}
              />
            </Group>
            <Footnote>
              {form.tracksInventory
                ? 'Каждая продажа забирает одну позицию со склада. Товар без остатка купить нельзя.'
                : 'Остатки не считаются: товар можно купить сколько угодно раз, выдача ляжет на оператора.'}
            </Footnote>
          </section>

          <FormGroup>
            <TextRow
              label="Порог «мало осталось»"
              id="p-threshold"
              value={form.lowStockThreshold}
              onChange={(value) => set('lowStockThreshold', value)}
              placeholder="5"
              inputMode="numeric"
            />
            <TextRow
              label="Лимит на покупателя"
              id="p-limit"
              value={form.purchaseLimitPerUser}
              onChange={(value) => set('purchaseLimitPerUser', value)}
              placeholder="Без лимита"
              inputMode="numeric"
            />
          </FormGroup>

          {editingId ? (
            <Group>
              <Row
                icon="key"
                title="Загрузить остатки"
                subtitle={
                  detail.data?.available === null
                    ? 'Учёт остатков выключен'
                    : `Сейчас ${detail.data?.available ?? 0}`
                }
                chevron
                onClick={() => onNavigate(`/admin/products/${editingId}/stock`)}
              />
            </Group>
          ) : (
            <Footnote>Склад можно будет загрузить сразу после сохранения.</Footnote>
          )}
        </div>
      ) : null}

      {section === 'preview' ? (
        <PreviewSection
          input={{
            title: form.title,
            subtitle: form.subtitle,
            description: form.description,
            activationInstructions: form.activationInstructions,
            price: form.price,
            listPrice: form.listPrice,
            currency,
            decimals,
            imageUrl: firstImage,
            available: detail.data?.available ?? null,
            variants: form.variants,
            attributes: form.attributes
              .filter((attribute) => attribute.name.trim() !== '')
              .map((attribute) => ({ name: attribute.name.trim(), value: attribute.value.trim() })),
            issues,
          }}
        />
      ) : null}

      {/*
       * The confirmation, and the two things that come next.
       *
       * Not a toast and not a redirect: a product that has just been created is
       * usually a product that still needs its stock, and the owner is the only
       * one who knows whether it does. Both offers are here, and staying put is
       * a valid answer.
       */}
      {saved ? (
        <section ref={savedRef}>
          <SectionLabel>Сохранено</SectionLabel>
          <Group>
            {saved.tracksInventory ? (
              <Row
                icon="key"
                title="Загрузить остатки"
                subtitle="Без них опубликованный товар не купят"
                chevron
                onClick={() => onNavigate(`/admin/products/${saved.id}/stock`)}
              />
            ) : null}
            {saved.published && saved.shortId ? (
              <Row
                icon="bag"
                title="Открыть как покупатель"
                chevron
                onClick={() => onNavigate(`/product/${saved.shortId}`)}
              />
            ) : null}
            <Row
              icon="inbox"
              title="К списку товаров"
              chevron
              onClick={() => onNavigate('/admin/products')}
            />
          </Group>
        </section>
      ) : null}

      <ActionBar error={error}>
        {blockers.length > 0 ? (
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <Notice tone="warning">
              {blockers.length === 1 ? (
                blockers[0]
              ) : (
                <ul style={{ margin: 0, paddingLeft: '1.1em' }}>
                  {blockers.map((blocker) => (
                    <li key={blocker}>{blocker}</li>
                  ))}
                </ul>
              )}
            </Notice>
          </div>
        ) : null}
        <Button
          block
          loading={busy}
          disabled={blockers.length > 0}
          onClick={() => {
            setSaved(null);
            setError(null);
            void save();
          }}
        >
          {editingId ? 'Сохранить' : 'Создать товар'}
        </Button>
      </ActionBar>
    </div>
  );
}
